// @flow

import get from 'lodash/get';

import { fileToDataUrl } from '../utils/fileUtils';
import { facebookAuth, pxcAuth, portalAuth, login, logout, token, setToken } from '../api/auth';
import * as accountApi from '../api/account';
import * as userApi from '../api/user';
import type { RequestPhoneVerificationPayload } from '../api/user';
import * as types from '../constants/actionTypes';
import {
  CLAIM_CREDIT,
  DELETE_PAYMENT_METHOD,
  DEPOSIT,
  FACEBOOK_SIGN_IN,
  INVITE_OTHERS,
  LIMITS,
  PASSWORD_RESET,
  PXC_SIGN_IN,
  RAZORPAY_DEPOSIT,
  REQUEST_EMAIL_VERIFICATION,
  REQUEST_PHONE_VERIFICATION,
  SELF_EXCLUDE,
  SMS_LOGIN,
  SMS_VERIFICATION,
  TAKE_A_BREAK,
  TOGGLE_COMMUNICATION_PREFERENCES,
  TRANSACTIONS,
  USER_PLAYUP_CAMPAIGN_STATUS,
  USER_CREATE_PLAYUP_ACCOUNT,
  UPDATE_PHONE,
  UPLOAD_DOCUMENT,
  USER_BALANCE,
  USER_DETAILS,
  USER_QUESTIONS,
  USER_SIGN_IN,
  USER_SIGN_OUT,
  USER_SIGN_UP,
  USER_TERMS,
  USER_UPDATE,
  USER_WITHDRAWAL,
  VERIFY_PHONE,
  WITHDRAWAL_ACCOUNTS,
  PORTAL_SIGN_IN
} from '../constants/apiTypes';
import { DEPOSITS, WINS, WITHDRAWALS } from '../constants/transactionTypes';
import { getReferrerCode, removeReferrerCode } from '../utils/userUtils';

export function initialiseUser() {
  return {
    type: types.INITIALISE_USER
  };
}

export function toggleDarkMode() {
  return (dispatch, getState) => {
    const { darkMode } = getState().settings;

    dispatch({
      type: types.TOGGLE_DARK_MODE,
      darkMode: !darkMode
    });
  };
}

export function agreeToTerms() {
  return {
    type: types.API_CALL,
    api: USER_TERMS,
    request: userApi.acceptTerms()
  };
}

export function acquireCredit() {
  return {
    type: types.API_CALL,
    api: CLAIM_CREDIT,
    request: userApi.claimCredit()
  };
}

export function fetchUserQuestions() {
  return {
    type: types.API_CALL,
    api: USER_QUESTIONS,
    request: userApi.getUserQuestions()
  };
}

export function requestPhoneVerification(data: RequestPhoneVerificationPayload) {
  return {
    type: types.API_CALL,
    api: REQUEST_PHONE_VERIFICATION,
    request: userApi.requestPhoneVerification(data)
  };
}

export function requestEmailVerification() {
  return {
    type: types.API_CALL,
    api: REQUEST_EMAIL_VERIFICATION,
    request: userApi.requestEmailVerification()
  };
}

export function toggleCommunicationPreference(data) {
  return {
    type: types.API_CALL,
    api: TOGGLE_COMMUNICATION_PREFERENCES,
    request: accountApi.toggleCommunicationPreferences(data)
  };
}

export function verifyPhone(code) {
  return {
    type: types.API_CALL,
    api: VERIFY_PHONE,
    request: userApi.verifyPhone(code)
  };
}

export function updatePhone(phoneNumber) {
  return {
    type: types.API_CALL,
    api: UPDATE_PHONE,
    request: userApi.updateCurrentUser({ phone: phoneNumber })
  };
}

export function getUserBalance() {
  return {
    type: types.API_CALL,
    api: USER_BALANCE,
    request: userApi.getUserDetails().then(async payload => ({
      ...payload,
      token: await token()
    }))
  };
}

export function getCurrentUser() {
  return {
    type: types.API_CALL,
    api: USER_DETAILS,
    request: userApi.getUserDetails().then(async payload => ({
      ...payload,
      token: await token()
    }))
  };
}

export function loginWithCurrentDetails() {
  return {
    type: types.API_CALL,
    api: USER_SIGN_IN,
    request: userApi.getUserDetails().then(payload => ({
      ...payload,
      token: token()
    }))
  };
}

export function loginWithTemporaryToken(loginToken) {
  const promise = login({
    login_token: loginToken
  })
    .then(userApi.getUserDetails)
    .then(payload => ({
      ...payload,
      token: token()
    }));

  return {
    type: types.API_CALL,
    api: USER_SIGN_IN,
    request: promise
  };
}

export function inviteOthers(data) {
  return {
    type: types.API_CALL,
    api: INVITE_OTHERS,
    request: userApi.inviteOthers(data)
  };
}

export function resetWithdrawDetails() {
  return {
    type: types.RESET_WITHDRAW_DETAILS
  };
}

export function getWithdrawalDetails() {
  return {
    type: types.API_CALL,
    api: WITHDRAWAL_ACCOUNTS,
    request: accountApi.getWithdrawalAccounts()
  };
}

export function createLimits(data) {
  return {
    type: types.API_CALL,
    api: LIMITS,
    request: accountApi.createLimits(data)
  };
}

export function userSignIn(email, password) {
  const promise = login({ email, password })
    .then(userApi.getUserDetails)
    .then(async payload => ({
      ...payload,
      token: await token()
    }));

  return {
    type: types.API_CALL,
    api: USER_SIGN_IN,
    request: promise
  };
}

export function resetUserErrors() {
  return {
    type: types.RESET_USER_ERRORS
  };
}

export function selfExclude() {
  return {
    type: types.API_CALL,
    api: SELF_EXCLUDE,
    request: accountApi.selfExclude()
  };
}

export function takeABreak(data) {
  return {
    type: types.API_CALL,
    api: TAKE_A_BREAK,
    request: accountApi.takeABreak(data)
  };
}

export function getCampaignStatus() {
  return {
    type: types.API_CALL,
    api: USER_PLAYUP_CAMPAIGN_STATUS,
    request: userApi.getPlayUpCampaignStatus()
  };
}

export function createPlayUpAccount(data) {
  return {
    type: types.API_CALL,
    api: USER_CREATE_PLAYUP_ACCOUNT,
    request: userApi.createPlayUpAccount(data)
  };
}

export function toggleCampaignModal(showCampaignModal) {
  return {
    type: types.USER_TOGGLE_CAMPAIGN_MODAL,
    showCampaignModal
  };
}

export function resetPlayUpCampaign() {
  return {
    type: types.RESET_PLAYUP_CAMPAIGN
  };
}

export function userSignOut() {
  return {
    type: types.API_CALL,
    api: USER_SIGN_OUT,
    request: logout()
  };
}

export function updateUserData(data = {}) {
  return {
    type: types.API_CALL,
    api: USER_UPDATE,
    request: userApi.updateCurrentUser(data)
  };
}

export function updateUserFromFormFields() {
  return (dispatch, getState) => {
    const { fields } = getState().formFields;

    const missingFields = Object.values(fields).filter(x => x.required && !x.value);

    if (missingFields.length > 0) {
      dispatch({
        type: types.API_CALL_FAILURE,
        api: USER_UPDATE,
        errors: {
          messages: missingFields.map(field => ({
            key: field.key,
            message: `${field.label} is a required field`
          }))
        }
      });
    } else {
      dispatch(
        updateUserData({
          address: get(fields, 'streetAddress.value'),
          city: get(fields, 'city.value'),
          email: get(fields, 'email.value'),
          first_name: get(fields, 'first_name.value'),
          last_name: get(fields, 'last_name.value'),
          name: get(fields, 'name.value'),
          phone: get(fields, 'phone.value'),
          postcode: get(fields, 'postcode.value'),
          state: get(fields, 'state.value')
        })
      );
    }
  };
}

export function updateUserRealName({ firstName, lastName }) {
  return {
    type: types.API_CALL,
    api: USER_UPDATE,
    request: userApi.updateCurrentUser({
      first_name: firstName,
      last_name: lastName
    })
  };
}

export function resetPassword(email) {
  return {
    type: types.API_CALL,
    api: PASSWORD_RESET,
    request: userApi.resetPassword(email)
  };
}

function encodeFormFields(fields) {
  const convertKey = key => {
    const lookup = { streetAddress: 'address' };
    return lookup[key] || key;
  };

  return Object.values(fields).reduce(
    (memo, item) => ({
      ...memo,
      [convertKey(item.key)]: item.value
    }),
    {}
  );
}

export function createUser() {
  return (dispatch, getState) => {
    const { formFields, route: { query } = {} } = getState();

    const userData = {
      ...encodeFormFields(formFields.fields),
      source: query ? get(query, 'utm_source') : undefined,
      referral_code: query ? get(query, 'referrer') : undefined
    };

    try {
      const affiliateToken = getReferrerCode(query);

      if (affiliateToken) {
        userData.affiliate_token = affiliateToken;
      }
    } catch (err) {
      // do nothing
    }

    const promise = userApi
      .createUser(userData)
      .then(() => {
        removeReferrerCode();

        return login({
          email: userData.email,
          password: userData.password
        });
      })
      .then(userApi.getUserDetails)
      .then(async payload => ({
        ...payload,
        token: await token()
      }));

    dispatch({
      type: types.API_CALL,
      api: USER_SIGN_UP,
      request: promise
    });
  };
}

export function resetDepositDetails() {
  return {
    type: types.RESET_DEPOSIT_DETAILS
  };
}

export function setUserQuestionAnswer(key, value) {
  return {
    type: types.SET_USER_QUESTION_ANSWER,
    key,
    value
  };
}

export function toggleSetDepositLimits() {
  return {
    type: types.SET_DEPOSIT_LIMITS_AFTERWARDS
  };
}

export function makeDeposit(depositDetails) {
  return {
    type: types.API_CALL,
    api: DEPOSIT,
    request: accountApi.makeDeposit(depositDetails)
  };
}

export function razorpayDeposit(depositDetails) {
  return {
    type: types.API_CALL,
    api: RAZORPAY_DEPOSIT,
    request: accountApi.razorpayDeposit(depositDetails)
  };
}

export function requestPaypalDeposit() {
  return {
    type: types.PAYPAL_DEPOSIT_REQUEST
  };
}

export function deletePaymentMethod(paymentMethodId) {
  return {
    type: types.API_CALL,
    api: DELETE_PAYMENT_METHOD,
    request: accountApi.deletePaymentMethod(paymentMethodId),
    id: paymentMethodId
  };
}

export function paypalDepositSuccess(data = {}) {
  return {
    type: types.PAYPAL_DEPOSIT_SUCCESS,
    status: get(data, 'payment_transaction.status')
  };
}

export function paypalDepositFailure(errors) {
  return {
    type: types.PAYPAL_DEPOSIT_FAILURE,
    errors
  };
}

export function depositSuccess({ amount, paymentType }) {
  return {
    type: types.DEPOSIT_SUCCESS,
    amount,
    paymentType
  };
}

export function withdrawWinnings(withdrawalDetails) {
  const promise = accountApi.withdrawWinnings(withdrawalDetails).then(res => ({
    ...res,
    amount: withdrawalDetails.amount
  }));
  return dispatch => {
    dispatch({
      type: types.API_CALL,
      api: USER_WITHDRAWAL,
      request: promise
    });
  };
}

export function uploadDocument(file) {
  return (dispatch, getState) => {
    const {
      user: { withdrawalDetails }
    } = getState();
    fileToDataUrl(file).then(dataUrl => {
      dispatch({
        type: types.API_CALL,
        api: UPLOAD_DOCUMENT,
        request: accountApi.uploadDocument(withdrawalDetails.id, {
          document: dataUrl
        })
      });
    });
  };
}

export function getTransactions(options = {}) {
  const query = {
    page: options.page,
    transaction_category_id: {
      deposits: DEPOSITS,
      withdrawals: WITHDRAWALS,
      wins: WINS
    }[options.type]
  };

  return {
    type: types.API_CALL,
    api: TRANSACTIONS,
    request: accountApi.getTransactions(query)
  };
}

export function verifySMSCode(code) {
  return {
    type: types.API_CALL,
    api: SMS_VERIFICATION,
    request: accountApi.verifySMSCode({ code })
  };
}

export function smsLogin(data = {}) {
  const { code, emailAddress, firstName, lastName, password } = data;

  return {
    type: types.API_CALL,
    api: SMS_LOGIN,
    request: accountApi
      .smsCodeLogin({
        code,
        email: emailAddress,
        first_name: firstName,
        last_name: lastName,
        password
      })
      .then(() =>
        login({
          email: emailAddress,
          password
        })
      )
  };
}

export function facebookSignIn(accessToken) {
  const promise = facebookAuth(accessToken)
    .then(userApi.getUserDetails)
    .then(payload => ({
      ...payload,
      token: token()
    }));

  return {
    type: types.API_CALL,
    api: FACEBOOK_SIGN_IN,
    request: promise
  };
}

export function pxcSignIn(accessToken) {
  const promise = pxcAuth(accessToken)
    .then(userApi.getUserDetails)
    .then(payload => ({
      ...payload,
      token: token()
    }));

  return {
    type: types.API_CALL,
    api: PXC_SIGN_IN,
    request: promise
  };
}

export function portalSignIn(authCode) {
  const promise = portalAuth(authCode)
    .then(() => {
      return userApi.getUserDetails();
    })
    .then(async payload => {
      return {
        ...payload,
        token: await token()
      };
    });

  return {
    type: types.API_CALL,
    api: PORTAL_SIGN_IN,
    request: promise
  };
}

// used to log in fantasy when we already ahve a valid JWT token
export function jwtSignin(fantasyJWT) {
  setToken(fantasyJWT);

  return {
    type: types.API_CALL,
    api: PORTAL_SIGN_IN,
    request: userApi.getUserDetails()
  };
}
